import { useCallback, useEffect, useState } from "react";
import WorkflowCard from "./WorkflowCard";
import { Workflow } from "./interface";
import { getData } from "./backend";
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import HeroIcon from "./HeroIcon";
import { INSTANT_WORKFLOW_SLUGS } from "./utils";

interface Props {
  token: string;
  onSelectWorkflow: (workflow: Workflow) => void;
}

function AppWorkflowList(props: Props) {
  const { t } = useTranslation();

  const { token } = props;
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [instantWorkflows, setInstantWorkflows] = useState<any[]>([]);

  const [total, setTotal] = useState(0);

  const [isLoading, setIsLoading] = useState(true);

  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [error, setError] = useState<Error>();

  const fetchWorkflows = useCallback(() => {
    if (!token)
      return;

    setIsLoading(true);

    const slugs = INSTANT_WORKFLOW_SLUGS.join(",");

    getData(`/hubspot/app/workflows/?page=${currentPage}&slugs=${slugs}`, token)
      .then(data => {
        setWorkflows(data.results || []);
        setTotal(data.total || 0);
        setNumPages(data.numPages || 1);
        setInstantWorkflows(data.instantWorkflows || []);
      })
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [token, currentPage]);

  useEffect(() => {
    fetchWorkflows();
  }, [token, fetchWorkflows]);

  return (
    <div className="flex flex-col items-center h-full">
      <div className="flex justify-between border-b mb-6 pb-2 w-full">
        <div className="text-lg font-semibold text-gray-600 font-gilroy">
          {t("Select Workflow")}
          {total > 0 &&
            <>&nbsp;({total})</>
          }
        </div>
        <div className="flex items-center flex-shrink-0 mt-auto" style={{ visibility: numPages === 0 ? "hidden" : "visible" }}>
          <ChevronLeftIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" onClick={() => setCurrentPage(i => Math.max(1, i - 1))} />
          <div className="font-semibold text-gray-600 font-gilroy w-12 text-center">
            {currentPage} / {numPages}
          </div>
          <ChevronRightIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" onClick={() => setCurrentPage(i => Math.min(numPages, i + 1))} />
        </div>
        <a className="btn btn-white text-sm h-8" href="https://app.portant.co" target="_blank" rel="noreferrer">
          {t("Open")} Portant <ExternalLinkIcon className="h-4 w-4 ml-2" />
        </a>
      </div>

      <div className="flex flex-col items-center overflow-y-auto h-full w-full">
        {isLoading &&
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <img src="https://static.portant.co/portant-loading-gray.svg" alt="Loading Logo" className='w-32 h-32' />
            <div className="text-gray-400 font-semibold text-center text-sm mt-2">{t("Loading Workflows")}...</div>
          </div>
        }
        {!isLoading &&
          <div className="flex flex-col w-full h-full gap-6">
            <div className="flex items-center gap-4 w-full">
              {instantWorkflows.map((workflow: Workflow) => (
                <button key={workflow.id} className="flex items-center justify-center gap-2 shadow border rounded py-4 w-1/3 font-semibold font-gilroy text-gray-600 cursor-pointer" onClick={() => props.onSelectWorkflow(workflow)}>
                  <HeroIcon icon={workflow.icon} className="w-6 h-6" />{workflow.name}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-8 gap-y-8 w-full">
              {workflows.map((workflow) => <WorkflowCard key={workflow.id} workflow={workflow} onSelect={props.onSelectWorkflow} />)}
            </div>
          </div>
        }
        {(!isLoading && error) &&
          <div className="flex flex-col w-full h-full gap-6 text-red text-center font-semibold">
            Something went wrong. Please contact support.
          </div>
        }
      </div>
    </div>
  );
}

export default AppWorkflowList;
